import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export default function Header() {
  const { t } = useTranslation('homepage');
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const isBardeau = location.pathname === '/bardeau-asphalte';
  const isInspection = location.pathname === '/inspection-toiture';
  const isDeneigement = location.pathname === '/deneigement-toiture';
  const isSoumission = location.pathname === '/soumission-gratuite';
  const isAPropos = location.pathname === '/a-propos';
  const isMentionsLegales = location.pathname === '/mentions-legales';
  const isPolitiqueConfidentialite = location.pathname === '/politique-confidentialite';
  const isBlog = location.pathname.startsWith('/blog');
  const isSecteur = location.pathname.startsWith('/secteurs');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateTo = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goHomeAndScroll = (id: string) => {
    navigateTo('/');
    setTimeout(() => scrollToSection(id), 300);
  };

  const handleQuoteClick = () => {
    if (isHome || isBardeau || isInspection || isDeneigement || isSoumission) {
      scrollToSection('quote-form');
    } else if (isAPropos || isBlog || isMentionsLegales || isPolitiqueConfidentialite || isSecteur) {
      navigateTo('/soumission-gratuite');
    } else {
      goHomeAndScroll('quote-form');
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (id: string) => {
    if (isHome) {
      scrollToSection(id);
    } else {
      goHomeAndScroll(id);
    }
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  };

  const navLinks = [
    { label: t('nav_home'), action: () => navigateTo('/') },
    {
      label: t('nav_services'),
      dropdown: [
        { label: t('service_refaction_title'), action: () => navigateTo('/refection-toiture-levis') },
        { label: t('service_toiture_title'), action: () => navigateTo('/toiture-levis') },
        { label: t('service_repair_title'), action: () => navigateTo('/reparation-toiture-levis') },
        { label: "Bardeau d'asphalte", action: () => navigateTo('/bardeau-asphalte') },
        { label: "Inspection toiture", action: () => navigateTo('/inspection-toiture') },
        { label: "Déneigement toiture", action: () => navigateTo('/deneigement-toiture') },
        { label: t('service_flat_title'), action: () => navigateTo('/toit-plat-membrane-elastomere') },
        { label: t('service_metal_title'), action: () => navigateTo('/toiture-tole-metal') },
        { label: t('service_inspection_title'), action: () => navigateTo('/inspection-toiture') },
        { label: t('service_snow_title'), action: () => navigateTo('/deneigement-toiture') },
      ],
    },
    { label: t('nav_about'), action: () => handleNavClick('whyus') },
    { label: t('nav_quote'), action: () => handleNavClick('cta-final') },
  ];

  const transparentHeader = isHome && !isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'bg-transparent' : 'bg-white shadow-md'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigateTo('/'); }}
            className={`font-manrope font-extrabold text-lg md:text-xl tracking-tight transition-colors ${
              (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'text-white' : 'text-slate-primary'
            }`}
          >
            Couvreur-Lévis.com
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link, idx) => (
              <div key={idx} className="relative">
                {link.dropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                        (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'text-white hover:text-copper' : 'text-slate-primary hover:text-copper'
                      }`}
                    >
                      {link.label}
                      <i className="ri-arrow-down-s-line text-xs" />
                    </button>
                    {isServicesOpen && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-soft-dark">
                        {link.dropdown.map((item, i) => (
                          <button
                            key={i}
                            onClick={item.action}
                            className="block w-full text-left px-4 py-2 text-sm text-slate-primary hover:bg-soft-gray hover:text-copper transition-colors"
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={link.action}
                    className={`text-sm font-medium transition-colors whitespace-nowrap ${
                      (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'text-white hover:text-copper' : 'text-slate-primary hover:text-copper'
                    }`}
                  >
                    {link.label}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* Right side: phone + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+14184769950"
            aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
            title="Cliquez pour appeler"
              aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
              title="Cliquez pour appeler"
              className={`flex items-center gap-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'text-white hover:text-copper' : 'text-slate-primary hover:text-copper'
              }`}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-copper/20">
                <i className="ri-phone-line text-copper" />
              </span>
              {t('phone_number')}
            </a>
            <button
              onClick={handleQuoteClick}
              className="bg-copper hover:bg-copper-dark text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-all duration-300 whitespace-nowrap"
            >
              {t('cta_quote')}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              (transparentHeader && !isBardeau && !isInspection && !isDeneigement && !isSoumission) ? 'text-white' : 'text-slate-primary'
            }`}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-soft-dark shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (link.action) link.action();
                  if (link.dropdown) setIsServicesOpen(!isServicesOpen);
                }}
                className="block w-full text-left text-slate-primary font-medium py-2 hover:text-copper transition-colors"
              >
                {link.label}
              </button>
            ))}
            <a
              href="tel:+14184769950"
            aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
            title="Cliquez pour appeler"
              aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
              title="Cliquez pour appeler"
              className="flex items-center gap-2 text-slate-primary font-semibold py-2"
            >
              <i className="ri-phone-line text-copper" />
              {t('phone_number')}
            </a>
            <button
              onClick={handleQuoteClick}
              className="w-full bg-copper text-white font-semibold py-3 rounded-md mt-2"
            >
              {t('cta_quote')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}